/**
 * Shared "normalize → log(dw:auth-error-map) → map to copy" wrapper for auth
 * surfaces (login, signup, OAuth). Single source of truth for the logging +
 * mapping glue so EmailLoginForm and LoginPage don't re-implement it (the
 * classification itself lives in the pure `humanizeAuthError.ts`).
 *
 * Kept OUT of `humanizeAuthError.ts` on purpose: that module is pure and
 * React/yjs-free so its Vitest + Playwright specs can import it directly. This
 * wrapper depends on `debugLog` (React/yjs), so it lives here. The caller passes
 * its react-i18next `t`, so the final copy is localized at the UI boundary.
 */
import { createNadDebug } from '../../utils/debugLog'
import {
  AuthErrorCtx,
  authErrorText,
  classifyAuthError,
  normalizeAuthError,
} from './humanizeAuthError'

// Faithful-error logging (CLAUDE.md #7): record the RAW provider error under the
// dw:auth-error-map feature tag before mapping, so `nad trace --feature
// auth-error-map` can explain exactly which status/code drove the user copy.
const errLog = createNadDebug('dw:auth-error-map')

type LogExtra = Record<string, string | number | boolean | null>

export function mapAuthError(
  t: (key: string, opts?: Record<string, string | number>) => string,
  ctx: AuthErrorCtx,
  error: object,
  extra?: LogExtra,
): string {
  const n = normalizeAuthError(error)
  const name = 'name' in error && typeof error.name === 'string' ? error.name : undefined
  const kind = classifyAuthError(error, ctx)
  errLog({
    event: 'auth-error-mapped',
    trace_id: 'auth-error-map',
    ctx,
    kind,
    status: n.status ?? null,
    code: n.code ?? null,
    name: name ?? null,
    message: n.message,
    ...(extra ?? {}),
  })
  return authErrorText(t, kind, n.message)
}
